import type { ReactNode, RefObject } from "react";
import type {
  DynastyRosterTeam,
  FindTradeRecommendation,
  PickPlayerAssignment,
  PickValueBucket,
  TradeAsset,
} from "./dynastyTradingPageUtils";
import { formatTransactionDate } from "./dynastyTradingPageUtils";

export function ImportedTeamsLayout({ children }: { children: ReactNode }) {
  return (
    <section
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 14,
        minHeight: 0,
        flex: 1,
      }}
    >
      {children}
    </section>
  );
}

export function TradeAssetCard(props: {
  asset: {
    id: string;
    name: string;
    position: string;
    team: string;
    value: number | null;
    imageUrl?: string;
    pickSeason?: number;
    pickRound?: number;
  };
  resolvedValue?: number | null;
  pickBucket?: "Early" | "Mid" | "Late" | null;
  onPickBucketChange?: (bucket: "Early" | "Mid" | "Late") => void;
  assignedRookie?: { name: string } | null;
  onToggleRookiePicker?: () => void;
  currentLeagueSeason?: number | null;
  onClick?: () => void;
  formatValue: (value: number | null) => string;
  getPositionColor: (value: string) => string;
}) {
  const {
    asset,
    resolvedValue,
    pickBucket,
    onPickBucketChange,
    assignedRookie,
    onToggleRookiePicker,
    currentLeagueSeason,
    onClick,
    formatValue,
    getPositionColor,
  } = props;
  const isPick = String(asset.position ?? "").toUpperCase() === "PI";
  const canAssignBucketValue =
    isPick &&
    asset.value == null &&
    asset.pickSeason != null &&
    asset.pickRound != null;
  const hasAssignedPickValue = canAssignBucketValue && Boolean(assignedRookie || pickBucket);
  const useCompactAssignedPickLayout = hasAssignedPickValue;
  const canAssignRookieValue =
    canAssignBucketValue &&
    currentLeagueSeason != null &&
    asset.pickSeason === currentLeagueSeason;

  return (
    <div
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: canAssignBucketValue && !useCompactAssignedPickLayout ? "minmax(0, 1fr)" : "minmax(0, 1fr) auto",
        gap: 8,
        alignItems: useCompactAssignedPickLayout ? "start" : "center",
        padding: "8px 10px",
        borderRadius: 10,
        border: "1px solid rgba(255,255,255,0.08)",
        background: "rgba(255,255,255,0.04)",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <div style={{ minWidth: 0, display: "flex", alignItems: useCompactAssignedPickLayout ? "flex-start" : "center", gap: 8 }}>
        {!isPick ? (
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              overflow: "hidden",
              flex: "0 0 24px",
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {asset.imageUrl ? (
              <img
                src={asset.imageUrl}
                alt={asset.name}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 900,
                  color: "rgba(255,255,255,0.7)",
                }}
              >
                {asset.name.slice(0, 1).toUpperCase()}
              </div>
            )}
          </div>
        ) : null}
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 11,
              fontWeight: 800,
              color: "var(--text-0)",
              whiteSpace: useCompactAssignedPickLayout ? "normal" : "nowrap",
              overflow: "hidden",
              textOverflow: useCompactAssignedPickLayout ? undefined : "ellipsis",
              display: useCompactAssignedPickLayout ? "-webkit-box" : "block",
              WebkitLineClamp: useCompactAssignedPickLayout ? 2 : undefined,
              WebkitBoxOrient: useCompactAssignedPickLayout ? "vertical" : undefined,
              lineHeight: useCompactAssignedPickLayout ? 1.15 : undefined,
            }}
          >
            {asset.name}
          </div>
          {!isPick ? (
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.52)", marginTop: 2 }}>
              <span style={{ color: getPositionColor(asset.position), fontWeight: 800 }}>{asset.position}</span>
              {asset.team ? <span>{` • ${asset.team}`}</span> : null}
            </div>
          ) : null}
        </div>
      </div>
      <div
        style={{
          display: "grid",
          justifyItems: "end",
          gap: canAssignBucketValue && !useCompactAssignedPickLayout ? 6 : 0,
          gridColumn: canAssignBucketValue && !useCompactAssignedPickLayout ? "1 / -1" : undefined,
          alignItems: useCompactAssignedPickLayout ? "start" : undefined,
        }}
      >
        <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.82)", paddingTop: useCompactAssignedPickLayout ? 1 : 0 }}>
          {formatValue(resolvedValue ?? asset.value)}
        </div>
        {canAssignBucketValue ? (
          <div
            style={{
              display: "grid",
              gap: 6,
              justifyItems: useCompactAssignedPickLayout ? "start" : "end",
              gridColumn: useCompactAssignedPickLayout ? "1 / -1" : undefined,
              marginTop: useCompactAssignedPickLayout ? -2 : 0,
            }}
          >
            {!hasAssignedPickValue ? (
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                {(["Early", "Mid", "Late"] as const).map((bucket) => (
                  <button
                    key={`${asset.id}-${bucket}`}
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onPickBucketChange?.(bucket);
                    }}
                    style={{
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: pickBucket === bucket ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.06)",
                      color: "var(--text-0)",
                      borderRadius: 999,
                      fontSize: 9,
                      fontWeight: 800,
                      padding: "3px 6px",
                      cursor: "pointer",
                    }}
                  >
                    {bucket}
                  </button>
                ))}
                {canAssignRookieValue ? (
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onToggleRookiePicker?.();
                    }}
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 999,
                      border: "1px solid rgba(255,255,255,0.12)",
                      background: "rgba(255,255,255,0.06)",
                      color: "var(--text-0)",
                      fontSize: 12,
                      fontWeight: 800,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                    aria-label={`Open rookie values for ${asset.name}`}
                  >
                    &#128269;
                  </button>
                ) : null}
              </div>
            ) : null}
            {assignedRookie ? (
              <div
                style={{
                  fontSize: 9,
                  color: "rgba(255,255,255,0.58)",
                  maxWidth: useCompactAssignedPickLayout ? "100%" : 170,
                  textAlign: useCompactAssignedPickLayout ? "left" : "right",
                  whiteSpace: useCompactAssignedPickLayout ? "normal" : undefined,
                  lineHeight: useCompactAssignedPickLayout ? 1.15 : undefined,
                }}
              >
                {assignedRookie.name}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function TeamAvatar(props: {
  team: { displayName: string; avatarUrl?: string };
  size?: number;
}) {
  const { team, size = 24 } = props;

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        overflow: "hidden",
        flex: `0 0 ${size}px`,
        background: "rgba(255,255,255,0.08)",
        border: "1px solid rgba(255,255,255,0.08)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {team.avatarUrl ? (
        <img
          src={team.avatarUrl}
          alt={team.displayName}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      ) : (
        <div
          style={{
            fontSize: Math.max(10, Math.floor(size * 0.42)),
            fontWeight: 900,
            color: "rgba(255,255,255,0.72)",
          }}
        >
          {team.displayName.slice(0, 1).toUpperCase()}
        </div>
      )}
    </div>
  );
}

export function TeamTradeHistoryPanel(props: {
  team: DynastyRosterTeam;
  compact?: boolean;
}) {
  const { compact = false, team } = props;
  const entries = team.tradeHistory ?? [];

  return (
    <div
      style={{
        padding: compact ? "10px 12px 6px" : "12px 12px 8px",
        borderTop: "1px solid rgba(255,255,255,0.08)",
        display: "grid",
        gap: compact ? 8 : 10,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 900,
          letterSpacing: 0.5,
          color: "rgba(255,255,255,0.52)",
          textTransform: "uppercase",
        }}
      >
        Trade History
      </div>

      {entries.length ? (
        entries.map((entry) => {
          const dateLabel = formatTransactionDate(entry.createdAt);
          const counterpartyLabel = entry.counterpartyNames.length
            ? entry.counterpartyNames.join(", ")
            : "League";
          const metaLabel = dateLabel && entry.leg
            ? `${dateLabel} • Wk ${entry.leg}`
            : dateLabel || (entry.leg ? `Wk ${entry.leg}` : "");

          return (
            <div
              key={`${team.rosterId}-trade-history-${entry.transactionId}`}
              style={{
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(255,255,255,0.03)",
                borderRadius: 12,
                padding: compact ? "8px 9px" : "9px 10px",
                display: "grid",
                gap: 8,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                <div
                  style={{
                    minWidth: 0,
                    fontSize: compact ? 10.5 : 11,
                    fontWeight: 800,
                    color: "var(--text-0)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  vs {counterpartyLabel}
                </div>
                <div
                  style={{
                    flex: "0 0 auto",
                    fontSize: 9,
                    color: "rgba(255,255,255,0.48)",
                    textAlign: "right",
                  }}
                >
                  {metaLabel}
                </div>
              </div>

              <div style={{ display: "grid", gap: 6 }}>
                <div style={{ display: "grid", gap: 3 }}>
                  <div style={{ fontSize: 9, fontWeight: 900, color: "#fca5a5", textTransform: "uppercase" }}>
                    Sent
                  </div>
                  <div style={{ fontSize: compact ? 9.5 : 10, color: "rgba(255,255,255,0.78)", lineHeight: 1.35 }}>
                    {entry.sent.length ? entry.sent.map((asset) => asset.label).join(", ") : "Nothing tracked"}
                  </div>
                </div>
                <div style={{ display: "grid", gap: 3 }}>
                  <div style={{ fontSize: 9, fontWeight: 900, color: "#86efac", textTransform: "uppercase" }}>
                    Received
                  </div>
                  <div style={{ fontSize: compact ? 9.5 : 10, color: "rgba(255,255,255,0.78)", lineHeight: 1.35 }}>
                    {entry.received.length ? entry.received.map((asset) => asset.label).join(", ") : "Nothing tracked"}
                  </div>
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div style={{ fontSize: compact ? 9.5 : 10.5, color: "rgba(255,255,255,0.46)", lineHeight: 1.35 }}>
          No completed Sleeper trades found for this league import.
        </div>
      )}
    </div>
  );
}

export function DynastyLeagueImportCard(props: {
  handleImportLeague: () => void;
  isImportingLeague: boolean;
  isNarrowViewport: boolean;
  leagueError: string;
  leagueIdentifier: string;
  setLeagueIdentifier: (value: string) => void;
}) {
  const {
    handleImportLeague,
    isImportingLeague,
    isNarrowViewport,
    leagueError,
    leagueIdentifier,
    setLeagueIdentifier,
  } = props;

  return (
    <section
      style={{
        border: "1px solid var(--border-0)",
        background: "var(--panel-bg)",
        borderRadius: 14,
        padding: 18,
        display: "grid",
        gap: 14,
        width: isNarrowViewport ? "100%" : "min(1000px, 50vw)",
        maxWidth: "100%",
        margin: "0 auto",
      }}
    >
      <div style={{ display: "grid", gap: 6 }}>
        <div style={{ fontSize: 24, fontWeight: 900, color: "var(--text-0)" }}>Sleeper Dynasty League Import</div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isNarrowViewport ? "minmax(0, 1fr)" : "minmax(420px, 1fr) minmax(180px, 320px)",
          gap: 12,
          alignItems: "center",
        }}
      >
        <input
          value={leagueIdentifier}
          onChange={(event) => setLeagueIdentifier(event.target.value)}
          placeholder="Sleeper league ID or URL"
          style={{
            width: "100%",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.06)",
            color: "var(--text-0)",
            padding: "12px 14px",
            outline: "none",
            boxSizing: "border-box",
            fontSize: 15,
          }}
        />

        <button
          type="button"
          onClick={handleImportLeague}
          disabled={isImportingLeague}
          style={{
            padding: "12px 16px",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.14)",
            background: isImportingLeague ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.14)",
            color: "var(--text-0)",
            fontWeight: 800,
            fontSize: 15,
            cursor: isImportingLeague ? "progress" : "pointer",
          }}
        >
          {isImportingLeague ? "Importing..." : "Import League"}
        </button>
      </div>

      <div
        style={{
          fontSize: 13,
          color: leagueError ? "rgb(248,113,113)" : "rgba(255,255,255,0.62)",
          minHeight: 18,
        }}
      >
        {leagueError || "Supports a Sleeper league URL or raw league ID."}
      </div>
    </section>
  );
}

export function MobileTradeMeterOverlay(props: {
  currentLeagueSeason: number | null;
  formatValue: (value: number | null) => string;
  getPositionColor: (value: string) => string;
  hideTradeCalculations?: boolean;
  leftDisplayTotal: number;
  leftIncomingAssets: TradeAsset[];
  leftTeam: DynastyRosterTeam;
  favoredTeamName: string;
  meterFillColor: string;
  meterFillLeft: number;
  meterFillRight: number;
  mobileTradeMeterRef: RefObject<HTMLDivElement | null>;
  onChangePickBucket: (assetId: string, bucket: PickValueBucket) => void;
  onRemoveTradeAsset: (targetTeamId: number, asset: TradeAsset) => void;
  onToggleRookiePicker: (assetId: string) => void;
  openInKtcHref?: string;
  pickRookieAssignments: Record<string, PickPlayerAssignment>;
  pickValueAssignments: Record<string, PickValueBucket>;
  premiumTeamRosterId: number;
  resolveTradeAssetValue: (asset: TradeAsset) => number | null;
  rightDisplayTotal: number;
  rightIncomingAssets: TradeAsset[];
  rightTeam: DynastyRosterTeam;
  valueAdjustment: number;
  addPlayerValue: number;
  twoTeamMeterCenterOffsetPx: number;
}) {
  const {
    addPlayerValue,
    currentLeagueSeason,
    favoredTeamName,
    formatValue,
    getPositionColor,
    hideTradeCalculations = false,
    leftDisplayTotal,
    leftIncomingAssets,
    leftTeam,
    meterFillColor,
    meterFillLeft,
    meterFillRight,
    mobileTradeMeterRef,
    onChangePickBucket,
    onRemoveTradeAsset,
    onToggleRookiePicker,
    openInKtcHref,
    pickRookieAssignments,
    pickValueAssignments,
    premiumTeamRosterId,
    resolveTradeAssetValue,
    rightDisplayTotal,
    rightIncomingAssets,
    rightTeam,
    twoTeamMeterCenterOffsetPx,
    valueAdjustment,
  } = props;

  return (
    <aside
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 20,
        background: "var(--dynasty-trade-panel-bg, rgba(20,28,46,0.98))",
        paddingBottom: "env(safe-area-inset-bottom, 0px)",
        boxShadow: "0 -12px 30px rgba(0,0,0,0.32)",
      }}
    >
      <div
        ref={mobileTradeMeterRef}
        style={{
          borderTop: "3px solid rgba(148,163,184,0.28)",
          boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
          display: "grid",
          gap: 12,
          padding: "14px 12px 10px",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 10,
            alignItems: "start",
          }}
        >
          {[
            {
              team: leftTeam,
              incomingAssets: leftIncomingAssets,
              displayReceiveValue: leftDisplayTotal,
              displayedValueAdjustment: leftTeam.rosterId === premiumTeamRosterId ? valueAdjustment : 0,
            },
            {
              team: rightTeam,
              incomingAssets: rightIncomingAssets,
              displayReceiveValue: rightDisplayTotal,
              displayedValueAdjustment: rightTeam.rosterId === premiumTeamRosterId ? valueAdjustment : 0,
            },
          ].map(({ team, incomingAssets, displayReceiveValue, displayedValueAdjustment }) => (
            <div
              key={`mobile-overlay-comparison-${team.rosterId}`}
              style={{
                display: "grid",
                gap: 8,
                minWidth: 0,
                alignContent: "start",
                borderLeft: team.rosterId === rightTeam.rosterId ? "2px solid rgba(148,163,184,0.22)" : "none",
                paddingLeft: team.rosterId === rightTeam.rosterId ? 12 : 0,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <TeamAvatar team={team} size={22} />
                <div
                  style={{
                    minWidth: 0,
                    fontSize: 13,
                    fontWeight: 900,
                    color: "var(--text-0)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {team.displayName}
                </div>
              </div>
              <div style={{ fontSize: 10, fontWeight: 900, color: "rgba(255,255,255,0.52)", textTransform: "uppercase" }}>
                Receive
              </div>
              {incomingAssets.length ? (
                incomingAssets.map((asset) => (
                  <TradeAssetCard
                    key={`mobile-overlay-receive-${team.rosterId}-${asset.sourceTeamId}-${asset.id}`}
                    asset={asset}
                    onClick={() => onRemoveTradeAsset(team.rosterId, asset)}
                    resolvedValue={resolveTradeAssetValue(asset)}
                    pickBucket={pickValueAssignments[asset.id] ?? null}
                    assignedRookie={pickRookieAssignments[asset.id] ?? null}
                    onToggleRookiePicker={() => onToggleRookiePicker(asset.id)}
                    onPickBucketChange={(bucket) => onChangePickBucket(asset.id, bucket)}
                    currentLeagueSeason={currentLeagueSeason}
                    formatValue={formatValue}
                    getPositionColor={getPositionColor}
                  />
                ))
              ) : (
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.42)" }}>No players selected</div>
              )}
              <div style={{ minWidth: 0, display: "grid", gap: 2 }}>
                {!hideTradeCalculations && displayedValueAdjustment > 0 ? (
                  <div style={{ fontSize: 10, fontWeight: 800, color: "rgba(255,255,255,0.52)" }}>
                    Value adjustment (+{formatValue(Math.round(displayedValueAdjustment))})
                  </div>
                ) : (
                  !hideTradeCalculations ? <div style={{ height: 14 }} /> : null
                )}
                {!hideTradeCalculations ? <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.78)" }}>
                  {formatValue(Math.round(displayReceiveValue))}
                </div> : null}
              </div>
            </div>
          ))}
        </div>

        {!hideTradeCalculations ? <div style={{ display: "grid", gap: 8 }}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
              gap: 10,
              alignItems: "end",
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.78)" }}>
              {formatValue(Math.round(leftDisplayTotal))}
            </div>
            <div style={{ fontSize: 11, fontWeight: 800, color: "rgba(255,255,255,0.78)", textAlign: "right" }}>
              {formatValue(Math.round(rightDisplayTotal))}
            </div>
          </div>
          <div
            style={{
              position: "relative",
              height: 16,
              borderRadius: 999,
              background: "rgba(255,255,255,0.08)",
              overflow: "hidden",
              border: "1px solid rgba(255,255,255,0.1)",
              marginLeft: twoTeamMeterCenterOffsetPx / 2,
              marginRight: -(twoTeamMeterCenterOffsetPx / 2),
            }}
          >
            <div
              style={{
                position: "absolute",
                inset: 0,
                background: `linear-gradient(90deg, transparent 0%, transparent ${meterFillLeft}%, ${meterFillColor} ${meterFillLeft}%, ${meterFillColor} ${meterFillRight}%, transparent ${meterFillRight}%, transparent 100%)`,
              }}
            />
            <div
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: "translate(-50%, -50%)",
                width: 4,
                height: 22,
                borderRadius: 999,
                background: "rgba(255,255,255,0.32)",
                boxShadow: "0 0 0 1px rgba(7,10,18,0.38)",
              }}
            />
          </div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 900,
              color: "var(--dynasty-accent, #60a5fa)",
              textAlign: "center",
            }}
          >
            {addPlayerValue > 0
              ? `${favoredTeamName} must add player worth ${formatValue(addPlayerValue)} for even trade.`
              : "Trade is even."}
          </div>
        </div> : null}

        {openInKtcHref ? (
          <a
            href={openInKtcHref}
            target="_blank"
            rel="noreferrer"
            style={{
              justifySelf: "center",
              border: "1px solid rgba(255,255,255,0.18)",
              background: "var(--dynasty-action-bg, rgba(255,255,255,0.92))",
              color: "var(--dynasty-action-text, #0f172a)",
              borderRadius: 10,
              padding: "9px 14px",
              fontSize: 12,
              fontWeight: 900,
              textDecoration: "none",
            }}
          >
            Open in KTC
          </a>
        ) : null}
      </div>
    </aside>
  );
}


export function FindTradePanel(props: {
  findTradeRecommendations: FindTradeRecommendation[];
  findTradeTargetPosition: string;
  formatValue: (value: number | null) => string;
  getPositionColor: (value: string) => string;
  hasMoreFindTradeRecommendations: boolean;
  importedTeams: DynastyRosterTeam[];
  isNarrowViewport: boolean;
  isTargetTeamMenuOpen: boolean;
  mobileFindTradeMenuRef?: RefObject<HTMLDivElement | null>;
  mobileFindTradeQuery: string;
  mobileFindTradeResults: Array<{ team: DynastyRosterTeam; player: { id: string; name: string; position: string; team: string } }>;
  onLoadMoreTrades: () => void;
  onSearchMobileFindTrade: (value: string) => void;
  onSeedRecommendedTrade: (recommendation: FindTradeRecommendation) => void;
  onSelectDirection: (value: "up" | "down") => void;
  onSelectMobileFindTradePlayer: (playerId: string, playerName: string, playerPosition: string) => void;
  onSelectTargetPosition: (value: "QB" | "RB" | "WR" | "TE") => void;
  onSelectTargetTeam: (value: string) => void;
  onToggleTargetTeamMenu: () => void;
  selectedFindTradePlayer: { id: string; name: string; position: string; value: number | null } | null;
  selectedFindTradeTeam: DynastyRosterTeam | null;
  selectedTargetTeam: DynastyRosterTeam | null;
  setIsTargetTeamMenuOpen: (value: boolean) => void;
  targetableTeams: DynastyRosterTeam[];
  visibleFindTradeRecommendations: FindTradeRecommendation[];
  findTradeDirection: "up" | "down";
  findTradePlayerId: string | null;
}) {
  const {
    findTradeDirection,
    findTradePlayerId,
    findTradeRecommendations,
    findTradeTargetPosition,
    formatValue,
    getPositionColor,
    hasMoreFindTradeRecommendations,
    importedTeams,
    isNarrowViewport,
    isTargetTeamMenuOpen,
    mobileFindTradeMenuRef,
    mobileFindTradeQuery,
    mobileFindTradeResults,
    onLoadMoreTrades,
    onSearchMobileFindTrade,
    onSeedRecommendedTrade,
    onSelectDirection,
    onSelectMobileFindTradePlayer,
    onSelectTargetPosition,
    onSelectTargetTeam,
    onToggleTargetTeamMenu,
    selectedFindTradePlayer,
    selectedFindTradeTeam,
    selectedTargetTeam,
    setIsTargetTeamMenuOpen,
    targetableTeams,
    visibleFindTradeRecommendations,
  } = props;

  return (
    <div
      style={{
        border: "1px solid var(--border-0)",
        background: "var(--panel-bg)",
        borderRadius: 14,
        boxShadow: "0 16px 34px rgba(0,0,0,0.18)",
        minHeight: 0,
        overflow: "hidden",
        height: "100%",
        display: "grid",
        gridTemplateRows: "auto auto 1fr",
      }}
    >
      <div style={{ padding: "14px 14px 12px", borderBottom: "1px solid rgba(255,255,255,0.08)", display: "grid", gap: 12 }}>
        {isNarrowViewport ? (
          <div ref={mobileFindTradeMenuRef} style={{ display: "grid", gap: 8, position: "relative" }}>
            <div style={{ fontSize: 11, fontWeight: 900, color: "rgba(255,255,255,0.56)", textTransform: "uppercase" }}>
              Find Player
            </div>
            <input
              value={mobileFindTradeQuery}
              onChange={(event) => onSearchMobileFindTrade(event.target.value)}
              placeholder="Search any roster"
              style={{
                width: "100%",
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(255,255,255,0.06)",
                color: "var(--text-0)",
                padding: "10px 12px",
                outline: "none",
                boxSizing: "border-box",
                fontSize: 16,
              }}
            />
            {mobileFindTradeQuery.trim() ? (
              <div style={{ display: "grid", gap: 6, maxHeight: 220, overflowY: "auto" }}>
                {mobileFindTradeResults.length ? (
                  mobileFindTradeResults.map(({ team, player }) => (
                    <button
                      key={`mobile-find-${team.rosterId}-${player.id}`}
                      type="button"
                      onClick={() => onSelectMobileFindTradePlayer(player.id, player.name, player.position)}
                      style={{
                        border: "1px solid rgba(255,255,255,0.08)",
                        background: player.id === findTradePlayerId ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.04)",
                        borderRadius: 10,
                        padding: "8px 10px",
                        textAlign: "left",
                        color: "var(--text-0)",
                        cursor: "pointer",
                        display: "grid",
                        gap: 2,
                      }}
                    >
                      <div style={{ fontSize: 12, fontWeight: 800 }}>{player.name}</div>
                      <div style={{ fontSize: 10, color: "rgba(255,255,255,0.56)" }}>
                        {team.displayName}
                        {player.team ? ` • ${player.team}` : ""}
                        {player.position ? ` • ${player.position}` : ""}
                      </div>
                    </button>
                  ))
                ) : (
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.48)" }}>No player found.</div>
                )}
              </div>
            ) : null}
          </div>
        ) : null}

        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 900, color: "rgba(255,255,255,0.56)", textTransform: "uppercase" }}>
            Direction
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
            {[
              ["up", "Trade Up", "Package current asset with another piece to get a stronger starter."],
              ["down", "Trade Down", "Move from one premium player into two useful assets."],
            ].map(([value, label, description]) => {
              const selected = findTradeDirection === value;
              return (
                <button
                  key={value}
                  type="button"
                  onClick={() => onSelectDirection(value as "up" | "down")}
                  style={{
                    border: selected ? "1px solid rgba(255,255,255,0.92)" : "1px solid rgba(255,255,255,0.12)",
                    background: selected ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.05)",
                    borderRadius: 12,
                    padding: "10px 12px",
                    color: "var(--text-0)",
                    textAlign: "left",
                    display: "grid",
                    gap: 6,
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: "50%",
                          border: selected ? "1px solid rgba(255,255,255,0.92)" : "1px solid rgba(255,255,255,0.32)",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {selected ? <div style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--text-0)" }} /> : null}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 900 }}>{label}</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.56)", lineHeight: 1.35 }}>{description}</div>
                </button>
              );
            })}
          </div>
        </div>

        <div style={{ display: "grid", gap: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 900, color: "rgba(255,255,255,0.56)", textTransform: "uppercase" }}>
            Target Position
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: 8 }}>
            {(["QB", "RB", "WR", "TE"] as const).map((position) => {
              const selected = findTradeTargetPosition === position;
              return (
                <button
                  key={position}
                  type="button"
                  onClick={() => onSelectTargetPosition(position)}
                  style={{
                    border: selected ? "1px solid rgba(255,255,255,0.92)" : "1px solid rgba(255,255,255,0.12)",
                    background: selected ? "rgba(255,255,255,0.92)" : "rgba(255,255,255,0.05)",
                    color: getPositionColor(position),
                    borderRadius: 10,
                    padding: "10px 0",
                    fontSize: 12,
                    fontWeight: 900,
                    cursor: "pointer",
                  }}
                >
                  {position}
                </button>
              );
            })}
          </div>
        </div>

        <div
          style={{ display: "grid", gap: 8, position: "relative" }}
          onBlur={(event) => {
            const nextFocused = event.relatedTarget as Node | null;
            if (nextFocused && event.currentTarget.contains(nextFocused)) return;
            setIsTargetTeamMenuOpen(false);
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 900, color: "rgba(255,255,255,0.56)", textTransform: "uppercase" }}>
            Target Team
          </div>
          <button
            type="button"
            onClick={onToggleTargetTeamMenu}
            style={{
              width: "100%",
              borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "rgba(255,255,255,0.06)",
              color: "var(--text-0)",
              padding: "10px 12px",
              fontSize: 12,
              fontWeight: 700,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              cursor: "pointer",
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              {selectedTargetTeam ? <TeamAvatar team={selectedTargetTeam} size={20} /> : null}
              <span style={{ minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {selectedTargetTeam ? selectedTargetTeam.displayName : "All teams"}
              </span>
            </span>
            <span style={{ color: "rgba(255,255,255,0.56)", fontSize: 10 }}>▼</span>
          </button>
          {isTargetTeamMenuOpen ? (
            <div
              style={{
                position: "absolute",
                top: "100%",
                left: 0,
                right: 0,
                marginTop: 6,
                borderRadius: 12,
                border: "1px solid rgba(255,255,255,0.12)",
                background: "rgba(18,22,34,0.98)",
                boxShadow: "0 18px 34px rgba(0,0,0,0.28)",
                overflow: "hidden",
                zIndex: 8,
                display: "grid",
              }}
            >
              <button
                type="button"
                onClick={() => onSelectTargetTeam("")}
                style={{
                  border: "none",
                  borderBottom: "1px solid rgba(255,255,255,0.06)",
                  background: "transparent",
                  color: "var(--text-0)",
                  padding: "10px 12px",
                  textAlign: "left",
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                }}
              >
                All teams
              </button>
              {targetableTeams.map((team) => (
                <button
                  key={`target-team-${team.rosterId}`}
                  type="button"
                  onClick={() => onSelectTargetTeam(String(team.rosterId))}
                  style={{
                    border: "none",
                    borderTop: "1px solid rgba(255,255,255,0.04)",
                    background: String(team.rosterId) === selectedTargetTeam?.rosterId?.toString() ? "rgba(255,255,255,0.08)" : "transparent",
                    color: "var(--text-0)",
                    padding: "10px 12px",
                    textAlign: "left",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                >
                  <TeamAvatar team={team} size={20} />
                  <span style={{ minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {team.displayName}
                  </span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.72)" }}>
          {selectedFindTradePlayer ? (
            <>
              Building around <span style={{ color: "var(--text-0)", fontWeight: 900 }}>{selectedFindTradePlayer.name}</span>{" "}
              from <span style={{ color: "var(--text-0)", fontWeight: 900 }}>{selectedFindTradeTeam?.displayName ?? "selected team"}</span>{" "}
              ({selectedFindTradePlayer.position}, {formatValue(selectedFindTradePlayer.value)}).
            </>
          ) : (
            isNarrowViewport ? "Search for one player from any team to start." : "Click one player in your roster to start."
          )}
        </div>
      </div>

      <div
        style={{
          padding: "10px 14px",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          fontSize: 11,
          fontWeight: 800,
          color: "rgba(255,255,255,0.58)",
        }}
      >
        {findTradeRecommendations.length
          ? `${findTradeRecommendations.length} recommended trades. Click any card to open both teams and seed the trade window.`
          : "Recommendations will appear here once you select a player and target position."}
      </div>

      <div
        className="dynastyRosterScroll"
        style={{
          minHeight: 0,
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
          padding: 14,
          paddingBottom: 18,
          display: "grid",
          gap: 10,
          alignContent: "start",
          borderBottomLeftRadius: 14,
          borderBottomRightRadius: 14,
        }}
      >
        {visibleFindTradeRecommendations.length ? (
          visibleFindTradeRecommendations.map((recommendation) => {
            const partnerTeam = importedTeams.find((team) => team.rosterId === recommendation.partnerTeamId);
            return (
              <button
                key={recommendation.id}
                type="button"
                onClick={() => onSeedRecommendedTrade(recommendation)}
                style={{
                  border: "1px solid rgba(255,255,255,0.1)",
                  background: "rgba(255,255,255,0.04)",
                  borderRadius: 14,
                  padding: "12px 12px 10px",
                  color: "var(--text-0)",
                  cursor: "pointer",
                  textAlign: "left",
                  display: "grid",
                  gap: 10,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 900 }}>
                    {(selectedFindTradeTeam?.displayName ?? "Selected Team")} vs {partnerTeam?.displayName ?? `Roster ${recommendation.partnerTeamId}`}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 900,
                      color:
                        recommendation.adjustedDelta > 0
                          ? "#4ade80"
                          : recommendation.adjustedDelta < 0
                            ? "#f87171"
                            : "rgba(255,255,255,0.74)",
                    }}
                  >
                    {recommendation.adjustedDelta > 0 ? "+" : recommendation.adjustedDelta < 0 ? "-" : ""}
                    {formatValue(Math.abs(Math.round(recommendation.adjustedDelta)))}
                  </div>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto minmax(0, 1fr)", gap: 10, alignItems: "start" }}>
                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 900, color: "rgba(255,255,255,0.52)", textTransform: "uppercase" }}>You Send</div>
                    {recommendation.myAssets.map((player) => (
                      <div key={`${recommendation.id}-send-${player.id}`} style={{ fontSize: 12, color: "rgba(255,255,255,0.88)" }}>
                        {player.name}
                        <span style={{ color: "rgba(255,255,255,0.48)" }}> • </span>
                        <span style={{ color: getPositionColor(player.position), fontWeight: 800 }}>{player.position}</span>
                        <span style={{ color: "rgba(255,255,255,0.48)" }}>{` • ${formatValue(player.value)}`}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ alignSelf: "center", fontSize: 14, fontWeight: 900, color: "rgba(255,255,255,0.46)" }}>for</div>
                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={{ fontSize: 10, fontWeight: 900, color: "rgba(255,255,255,0.52)", textTransform: "uppercase" }}>You Receive</div>
                    {recommendation.partnerAssets.map((player) => (
                      <div key={`${recommendation.id}-receive-${player.id}`} style={{ fontSize: 12, color: "rgba(255,255,255,0.88)" }}>
                        {player.name}
                        <span style={{ color: "rgba(255,255,255,0.48)" }}> • </span>
                        <span style={{ color: getPositionColor(player.position), fontWeight: 800 }}>{player.position}</span>
                        <span style={{ color: "rgba(255,255,255,0.48)" }}>{` • ${formatValue(player.value)}`}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </button>
            );
          })
        ) : (
          <div
            style={{
              minHeight: 180,
              border: "1px dashed rgba(255,255,255,0.12)",
              borderRadius: 14,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              padding: 20,
              color: "rgba(255,255,255,0.5)",
              fontSize: 12,
              fontWeight: 700,
              background: "rgba(255,255,255,0.02)",
            }}
          >
            {!selectedFindTradePlayer
              ? isNarrowViewport
                ? "Search for one player from any team to start finding trades."
                : "Select one player from your open roster to start finding trades."
              : !findTradeTargetPosition
                ? "Choose a target position to generate trade ideas."
                : "No close trade matches found yet for that setup."}
          </div>
        )}
        {hasMoreFindTradeRecommendations ? (
          <button
            type="button"
            onClick={onLoadMoreTrades}
            style={{
              border: "none",
              background: "transparent",
              padding: "2px 0 6px",
              color: "#60a5fa",
              fontSize: 12,
              fontWeight: 800,
              cursor: "pointer",
              textAlign: "center",
              textDecoration: "underline",
            }}
          >
            load more trades
          </button>
        ) : visibleFindTradeRecommendations.length ? (
          <div
            style={{
              padding: "2px 0 6px",
              color: "rgba(255,255,255,0.52)",
              fontSize: 12,
              fontWeight: 800,
              textAlign: "center",
            }}
          >
            no more trades
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function PlayerLookupPanel(props: {
  isNarrowViewport: boolean;
  isOpen: boolean;
  onClear: () => void;
  onFocus: () => void;
  onLookupResultClick: (rosterId: number) => void;
  onQueryChange: (value: string) => void;
  playerLookupQuery: string;
  results: Array<{
    team: DynastyRosterTeam;
    player: {
      id: string;
      name: string;
      position: string;
      team: string;
    };
  }>;
  searchContainerRef?: RefObject<HTMLDivElement | null>;
}) {
  const {
    isNarrowViewport,
    isOpen,
    onClear,
    onFocus,
    onLookupResultClick,
    onQueryChange,
    playerLookupQuery,
    results,
    searchContainerRef,
  } = props;

  return (
    <div
      ref={searchContainerRef}
      style={{ display: "grid", gap: 8, position: "relative" }}
      onBlur={
        isNarrowViewport
          ? undefined
          : (event) => {
              const nextFocused = event.relatedTarget as Node | null;
              if (nextFocused && event.currentTarget.contains(nextFocused)) return;
              onClear();
            }
      }
    >
      <div style={{ fontSize: isNarrowViewport ? 9 : 10, fontWeight: 900, color: "rgba(255,255,255,0.52)", textTransform: "uppercase" }}>
        Player Lookup
      </div>
      <div style={{ position: "relative" }}>
        <input
          value={playerLookupQuery}
          onChange={(event) => onQueryChange(event.target.value)}
          onFocus={onFocus}
          placeholder="Search player or pick"
          style={{
            width: "100%",
            borderRadius: 10,
            border: "1px solid rgba(255,255,255,0.12)",
            background: "rgba(255,255,255,0.06)",
            color: "var(--text-0)",
            padding: isNarrowViewport ? "9px 34px 9px 12px" : "10px 34px 10px 12px",
            outline: "none",
            boxSizing: "border-box",
            fontSize: isNarrowViewport ? 16 : 12,
          }}
        />
        {playerLookupQuery ? (
          <button
            type="button"
            onClick={onClear}
            aria-label="Clear player lookup"
            style={{
              position: "absolute",
              right: 8,
              top: "50%",
              transform: "translateY(-50%)",
              width: 18,
              height: 18,
              borderRadius: 999,
              border: "none",
              background: "rgba(255,255,255,0.12)",
              color: "rgba(255,255,255,0.78)",
              fontSize: 12,
              fontWeight: 900,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
              padding: 0,
            }}
          >
            ×
          </button>
        ) : null}
      </div>
      {playerLookupQuery.trim() && isOpen ? (
        <div style={{ display: "grid", gap: 6 }}>
          {results.length ? (
            results.map(({ team, player }) => (
              <button
                key={`lookup-${team.rosterId}-${player.id}`}
                type="button"
                onClick={() => onLookupResultClick(team.rosterId)}
                style={{
                  border: "1px solid rgba(255,255,255,0.08)",
                  background: "rgba(255,255,255,0.04)",
                  borderRadius: 10,
                  padding: "8px 10px",
                  textAlign: "left",
                  color: "var(--text-0)",
                  cursor: "pointer",
                  display: "grid",
                  gap: 2,
                }}
              >
                <div style={{ fontSize: 12, fontWeight: 800 }}>{player.name}</div>
                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.56)" }}>
                  {team.displayName}
                  {player.team ? ` • ${player.team}` : ""}
                  {player.position ? ` • ${player.position}` : ""}
                </div>
              </button>
            ))
          ) : (
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.48)" }}>No player found.</div>
          )}
        </div>
      ) : null}
    </div>
  );
}
